import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminApi, api } from "@/lib/api";
import { toast } from "sonner";
import {
  ChefHat,
  ClipboardList,
  Utensils,
  Star,
  LogOut,
  Home as HomeIcon,
  Search,
  Sparkles,
} from "lucide-react";

const TOKEN_KEY = "bk_admin_token";

export default function OpsHub() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [passcode, setPasscode] = useState("");
  const navigate = useNavigate();

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/login", { passcode });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Welcome, boss");
    } catch {
      toast.error("Invalid passcode");
    }
  };

  if (!token) {
    return (
      <div className="min-h-[100dvh] bg-stone-900 flex items-center justify-center p-6">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto rounded-full bg-stone-900 flex items-center justify-center mb-3">
              <ChefHat className="w-8 h-8 text-brand-yellow" />
            </div>
            <div className="font-display text-2xl text-stone-900">Blossom&apos;s Ops</div>
            <div className="text-xs text-stone-500 uppercase tracking-widest">
              Staff dashboard
            </div>
          </div>
          <form onSubmit={login} className="space-y-3">
            <input
              type="password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Passcode"
              required
              data-testid="ops-login-input"
              className="w-full px-3 py-3 rounded-lg border border-stone-300 outline-none focus:border-stone-900 text-base"
            />
            <button
              type="submit"
              data-testid="ops-login-submit"
              className="w-full bg-stone-900 text-white py-3 rounded-full font-bold uppercase text-sm tracking-widest hover:bg-stone-800"
            >
              Enter Ops
            </button>
            <Link to="/" className="block text-center text-xs text-stone-400 hover:text-stone-600 pt-2">
              ← Back to customer app
            </Link>
          </form>
        </div>
      </div>
    );
  }

  const tiles = [
    { label: "Kitchen Display", desc: "Live orders board with bump buttons", Icon: ChefHat, to: "/kitchen", color: "from-brand-red to-red-800" },
    { label: "Orders & Menu", desc: "Manage orders + edit menu items", Icon: ClipboardList, to: "/admin", color: "from-stone-700 to-stone-900" },
    { label: "Menu", desc: "Add / hide / edit items", Icon: Utensils, to: "/admin", color: "from-amber-700 to-amber-900" },
  ];

  return (
    <div className="min-h-[100dvh] bg-stone-900 text-white">
      <div className="chalkboard-band border-b-4 border-brand-red">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-brand-yellow flex items-center justify-center">
              <ChefHat className="w-5 h-5 text-stone-900" />
            </div>
            <div>
              <div className="font-display text-brand-yellow text-2xl leading-none">
                Blossom&apos;s Ops
              </div>
              <div className="text-[10px] uppercase tracking-widest text-white/60 mt-0.5">
                Staff hub
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="text-white/70 hover:text-white text-xs uppercase tracking-widest flex items-center gap-1 px-3 py-2"
              data-testid="ops-storefront-link"
            >
              <HomeIcon className="w-4 h-4" /> Customer App
            </Link>
            <button
              onClick={() => {
                localStorage.removeItem(TOKEN_KEY);
                setToken("");
                navigate("/ops");
              }}
              className="text-white/70 hover:text-white text-xs uppercase tracking-widest flex items-center gap-1 px-3 py-2"
              data-testid="ops-logout-btn"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          {tiles.map((t) => (
            <Link
              key={t.label}
              to={t.to}
              data-testid={`ops-tile-${t.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={`bg-gradient-to-br ${t.color} rounded-2xl p-6 hover:scale-[1.01] active:scale-[0.99] transition-transform shadow-lg`}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                  <t.Icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-2xl leading-tight">{t.label}</div>
                  <div className="text-sm text-white/80 mt-1">{t.desc}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <LoyaltyLookup token={token} />

        <div className="text-[11px] text-white/40 text-center">
          Tip: Install this page to your home screen for a one-tap Ops app icon (dark cow) —
          separate from your customer app.
        </div>
      </div>
    </div>
  );
}

function LoyaltyLookup({ token }) {
  const [phone, setPhone] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 7) {
      toast.error("Enter a valid phone number");
      return;
    }
    setLoading(true);
    try {
      const { data } = await adminApi(token).get(`/admin/loyalty/lookup?phone=${digits}`);
      setResult(data);
    } catch {
      toast.error("Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="ops-loyalty-lookup">
      <div className="flex items-center gap-2 mb-4">
        <Star className="w-5 h-5 text-brand-yellow" />
        <div className="font-display text-xl">Loyalty Lookup</div>
      </div>
      <form onSubmit={search} className="flex gap-2 flex-wrap">
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Customer phone number"
          data-testid="ops-loyalty-phone-input"
          className="flex-1 min-w-[180px] px-3 py-2.5 rounded-lg bg-stone-800 border border-stone-700 outline-none focus:border-brand-yellow text-sm"
        />
        <button
          type="submit"
          disabled={loading || phone.replace(/\D/g, "").length < 7}
          data-testid="ops-loyalty-search-btn"
          className="bg-brand-yellow text-stone-900 px-5 py-2.5 rounded-lg font-black uppercase text-xs tracking-widest hover:bg-yellow-400 disabled:opacity-40 flex items-center gap-1"
        >
          <Search className="w-4 h-4" />
          {loading ? "..." : "Look up"}
        </button>
      </form>
      {result && (
        <div className="mt-4 bg-stone-800 rounded-xl p-4 flex items-center gap-4" data-testid="ops-loyalty-result">
          <div className="w-12 h-12 rounded-full bg-brand-red flex items-center justify-center shrink-0">
            {result.exists ? (
              <Sparkles className="w-6 h-6 text-brand-yellow" />
            ) : (
              <Star className="w-6 h-6 text-stone-500" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            {result.exists ? (
              <>
                <div className="font-display text-2xl text-brand-yellow">
                  {result.points} pts
                </div>
                <div className="text-xs text-white/70 mt-0.5">
                  {result.name ? `${result.name} · ` : ""}
                  Earned {result.lifetime_earned} · Redeemed {result.lifetime_redeemed}
                </div>
              </>
            ) : (
              <div className="text-sm text-white/70">
                No loyalty account for that phone yet.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
